// K6 Phase 3 — remote commands on the device page (super admin).
// One dialog per code; the phone executes on its next heartbeat and acks with
// a result. Money stays on the phone: a forced end is PRICED there by the
// reason (StopPricing) and reported back as a session row — this UI only
// carries the reason, a note, and (for owner discretion) the chosen amount.
import { useEffect, useState } from 'react';
import { OctagonX, PowerOff, Power, Coins, Percent, MapPin, KeyRound, Stethoscope, Copy, Eye, Terminal } from 'lucide-react';
import { toast } from 'sonner';
import ConfirmModal from '../components/ConfirmModal';
import { issueCommand, getDiagnostics, newClientKey, errMsg, errDetail } from './kioskApi';
import { RED, GREEN, GREY, AMBER, ModalShell, primaryBtn, ghostBtn, inputStyle, labelStyle, fmtMs, fmtCentavos } from './KioskDialogs';

// Reason → how the phone prices it (K6-PLAN Phase 3 table). Shown beside each
// reason so the admin knows what the customer is charged before sending.
export const FE_REASONS = [
  { key: 'VIOLATION', label: 'Terms violation', pricing: 'Full booking' },
  { key: 'MACHINE_FAULT', label: 'Machine problem', pricing: 'Pro-rated · free inside 10 min · owner emailed' },
  { key: 'EMERGENCY', label: 'Emergency', pricing: 'Pro-rated · free inside 10 min · owner emailed' },
  { key: 'ABANDONED', label: 'Abandoned', pricing: 'Full booking' },
  { key: 'OTHER', label: 'Owner discretion', pricing: 'Your amount' },
];
export const FE_LABEL = Object.fromEntries(FE_REASONS.map(r => [r.key, r.label]));

// Phase 3b — why a push nudge did not reach the phone, in the reader's words.
// `reason` comes from the server's nudge record (device.push.last / command.nudge).
export const NUDGE_REASON = {
  no_token: 'the phone has not shared a push token yet',
  no_access_token: 'the server could not sign in to Google in time',
  network: 'the server could not reach Google',
  unregistered: 'the phone’s push token was stale; it re-sends one on its next heartbeat',
  fcm_error: 'Google refused the message',
  push_off: 'push is off on the server',
  server_off: 'the server has no Firebase key',
  lookup: 'a server error while looking the phone up',
  error: 'a server error',
};
export const nudgeReason = (n) => (n?.reason && NUDGE_REASON[n.reason]) || (n?.reason ? `${n.reason}` : 'unknown') + (n?.status ? ` (${n.status})` : '');

// One line about a nudge: "nudged 3:57:26 PM" or "no nudge: <why>". Null when nothing was recorded.
export function describeNudge(n) {
  if (!n || typeof n !== 'object') return null;
  if (n.sent) return { ok: true, text: `nudged ${fmtMs(n.at_ms)}${n.attempts > 1 ? ' on the 2nd try' : ''}` };
  return { ok: false, text: `no nudge: ${nudgeReason(n)}` };
}

// Device-level push line for the header: can push reach this phone, and what did the last nudge do.
export function describePush(push) {
  if (!push) return null;
  if (!push.ok) return { ok: false, text: `Push off: ${NUDGE_REASON[push.why] || 'not available'}` };
  const last = describeNudge(push.last);
  if (!last) return { ok: true, text: 'Push on: no nudge sent yet' };
  return last.ok ? { ok: true, text: `Push on: last ${last.text}` } : { ok: false, text: `Push on, but the last nudge failed ${fmtMs(push.last.at_ms)}: ${nudgeReason(push.last)}` };
}

export const CODE_LABEL = {
  FE: 'Force end', OOS1: 'Out of service', OOS0: 'Back in service', RATE: 'Set rate', SHARE: 'Set share',
  SITE: 'Set court name', PIN: 'Set staff PIN', DIAG: 'Diagnostics',
};

// pending / delivered / acked ok / rejected / failed / expired / superseded
export function commandState(c) {
  if (c.status === 'expired' && c.result === 'superseded') return { key: 'superseded', label: 'Superseded', color: GREY };
  if (c.status === 'expired') return { key: 'expired', label: 'Expired', color: GREY };
  if (c.status === 'done') return { key: 'ok', label: 'Acked ok', color: GREEN };
  if (c.status === 'failed') {
    const rejected = /reject|no session|refus|not allowed|invalid/i.test(String(c.error || ''));
    return rejected ? { key: 'rejected', label: 'Rejected', color: '#FF2D55' } : { key: 'failed', label: 'Failed', color: '#FF2D55' };
  }
  if (c.status === 'delivered') return { key: 'delivered', label: 'Delivered', color: AMBER };
  return { key: 'pending', label: 'Pending', color: AMBER };
}

export function describeArgs(c) {
  const a = c.args || {};
  switch (c.code) {
    case 'FE': return [FE_LABEL[a.reason] || a.reason, a.amount_centavos != null ? fmtCentavos(a.amount_centavos) : null, a.note].filter(Boolean).join(' · ');
    case 'OOS1': return a.message || '';
    case 'RATE': return `${fmtCentavos(a.centavos_per_hour)} / h`;
    case 'SHARE': return `owner ${fmtCentavos(a.owner_centavos_per_hour)} / h`;
    case 'SITE': return a.site_name || '';
    case 'PIN': return '••••••';           // the server stores it redacted
    case 'DIAG': return `${a.lines ?? 200} lines`;
    default: return '';
  }
}

function describeResult(c) {
  if (c.status === 'failed') return c.error || 'failed';
  if (c.status === 'expired') return c.result === 'superseded' ? 'a newer command replaced it' : 'the phone never picked it up';
  if (c.status !== 'done') return '';
  const r = c.result;
  if (r == null || r === true) return 'ok';
  if (typeof r === 'string') return r;
  if (c.code === 'DIAG' && r.bytes != null) return `${Math.round(r.bytes / 1024)} KB${r.truncated ? ' (truncated)' : ''}`;
  if (c.code === 'FE' && r.charged_centavos != null) return `charged ${fmtCentavos(r.charged_centavos)}${r.elapsed_minutes != null ? ` for ${r.elapsed_minutes} min` : ''}`;
  try { return JSON.stringify(r).slice(0, 120); } catch { return 'ok'; }
}

// Sends one command; on the fleet guard's 429 asks the second confirmation
// and resends with fleet_confirm. A stable client_key per dialog makes a
// retry after a network error idempotent.
function useIssue(deviceId, onDone) {
  const [busy, setBusy] = useState(false);
  const [fleetAsk, setFleetAsk] = useState(null);   // { body, devices }
  const [clientKey] = useState(newClientKey);

  const send = async (body, { fleetConfirm = false } = {}) => {
    setBusy(true);
    try {
      const r = await issueCommand(deviceId, { ...body, client_key: clientKey, fleet_confirm: fleetConfirm || undefined });
      toast.success(r.replayed ? 'Already sent' : `${CODE_LABEL[body.code]} sent — the command history shows when the phone was nudged and what it answered${r.superseded ? ` (replaced ${r.superseded} older)` : ''}`);
      onDone?.(r);
      return true;
    } catch (e) {
      const d = errDetail(e);
      if (e?.response?.status === 429 && d?.code === 'fleet_confirm') { setFleetAsk({ body, devices: d.devices, minutes: d.window_minutes }); return false; }
      toast.error((d && d.message) || errMsg(e, 'Could not send the command'));
      return false;
    } finally { setBusy(false); }
  };

  const fleetModal = fleetAsk ? (
    <ConfirmModal title="Whole fleet?" color={AMBER} confirmLabel="Yes, send it"
      message={`You have pushed settings to ${fleetAsk.devices} other devices in the last ${fleetAsk.minutes || 10} minutes. This is the guard against a fleet-wide slip — confirm you mean this device too.`}
      onConfirm={() => { const b = fleetAsk.body; setFleetAsk(null); send(b, { fleetConfirm: true }); }}
      onCancel={() => setFleetAsk(null)} />
  ) : null;
  return { send, busy, fleetModal };
}

const pesosToCentavos = (s) => {
  const n = Number(String(s).replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) ? Math.round(n * 100) : NaN;
};
const hint = { fontSize: 11, color: '#555', display: 'block', marginTop: 4 };
const warn = { fontSize: 12, color: AMBER, background: AMBER + '12', border: `1px solid ${AMBER}40`, borderRadius: 8, padding: '8px 10px', marginTop: 10, lineHeight: 1.5 };

function Footer({ onClose, onSend, can, busy, label, testId }) {
  return (
    <div className="flex gap-3 mt-6">
      <button onClick={onClose} data-testid={`${testId}-cancel`} style={ghostBtn}>Cancel</button>
      <button onClick={onSend} disabled={!can || busy} data-testid={`${testId}-send`} style={primaryBtn(!can || busy)}>{busy ? 'Sending…' : label}</button>
    </div>
  );
}

// ── Force end ─────────────────────────────────────────────────────────────────
export function ForceEndDialog({ device, onClose, onDone }) {
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [amount, setAmount] = useState('');
  const [called, setCalled] = useState(false);
  const { send, busy, fleetModal } = useIssue(device.kiosk_id, onDone);
  const cents = reason === 'OTHER' ? pesosToCentavos(amount) : null;
  const can = reason && called && (reason !== 'OTHER' || (Number.isInteger(cents) && cents >= 0));
  const submit = async () => {
    if (!can) return;
    const args = { reason };
    if (note.trim()) args.note = note.trim().slice(0, 200);
    if (reason === 'OTHER') args.amount_centavos = cents;
    if (await send({ code: 'FE', args, confirmed_call: true })) onClose();
  };
  return (
    <ModalShell title="Force end" onClose={onClose} testId="kiosk-cmd-fe">
      <p style={{ fontSize: 12, color: '#a3a3a3', lineHeight: 1.5 }}>
        Ends the running session on <b style={{ color: '#fff' }}>{device.name}</b>. The phone prices it by the reason and shows the reason on screen with &quot;Ask the court staff&quot;. Expires in 15 minutes if the phone is offline; rejected if nothing is running.
      </p>
      {!device.session && <p style={warn}>No session was reported on the last heartbeat — the phone will reject this unless one started since.</p>}
      <p style={{ ...labelStyle, marginTop: 14 }}>Reason</p>
      <div style={{ marginTop: 6, display: 'grid', gap: 6 }} data-testid="kiosk-cmd-fe-reasons">
        {FE_REASONS.map(r => (
          <label key={r.key} data-testid={`kiosk-cmd-fe-reason-${r.key}`} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
            background: reason === r.key ? RED + '14' : '#0A0A0A', border: `1px solid ${reason === r.key ? RED : '#2A2A2A'}`, borderRadius: 8, padding: '9px 12px' }}>
            <input type="radio" name="fe-reason" value={r.key} checked={reason === r.key} onChange={() => setReason(r.key)} />
            <span style={{ flex: 1, fontSize: 14, color: '#fff', fontWeight: 600 }}>{r.label}</span>
            <span style={{ fontSize: 11, color: r.pricing === 'Full booking' ? '#fff' : r.pricing === 'Your amount' ? AMBER : GREEN }}>{r.pricing}</span>
          </label>
        ))}
      </div>
      {reason === 'OTHER' && (
        <label style={{ display: 'block', marginTop: 12 }}>
          <span style={labelStyle}>Amount to charge (₱)</span>
          <input data-testid="kiosk-cmd-fe-amount" value={amount} onChange={e => setAmount(e.target.value)} placeholder="e.g. 100" inputMode="decimal" style={{ ...inputStyle, marginTop: 6 }} />
          <span style={hint}>The phone records this as an owner override (bearer OWNER). 0 is allowed.</span>
        </label>
      )}
      <label style={{ display: 'block', marginTop: 12 }}>
        <span style={labelStyle}>Note (optional)</span>
        <input data-testid="kiosk-cmd-fe-note" value={note} onChange={e => setNote(e.target.value)} maxLength={200} placeholder="What the court told you" style={{ ...inputStyle, marginTop: 6 }} />
      </label>
      <label data-testid="kiosk-cmd-fe-called" style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 14, cursor: 'pointer', fontSize: 13, color: '#fff' }}>
        <input type="checkbox" checked={called} onChange={e => setCalled(e.target.checked)} style={{ marginTop: 3 }} />
        <span>I have called the court. <span style={{ color: GREY }}>Required — someone on site must know the machine is about to stop.</span></span>
      </label>
      <Footer onClose={onClose} onSend={submit} can={can} busy={busy} label="Force end" testId="kiosk-cmd-fe" />
      {fleetModal}
    </ModalShell>
  );
}

// ── Out of service / back in service ─────────────────────────────────────────
export function OutOfServiceDialog({ device, onClose, onDone }) {
  const [message, setMessage] = useState(device.config?.out_of_service_message || '');
  const { send, busy, fleetModal } = useIssue(device.kiosk_id, onDone);
  const can = message.trim().length > 0 && message.trim().length <= 120;
  const submit = async () => { if (can && await send({ code: 'OOS1', args: { message: message.trim() } })) onClose(); };
  return (
    <ModalShell title="Out of service" onClose={onClose} testId="kiosk-cmd-oos1">
      <p style={{ fontSize: 12, color: '#a3a3a3', lineHeight: 1.5 }}>
        The welcome screen refuses new sessions and shows this message. A running session finishes normally. The court can clear it on the phone with the staff PIN; you can clear it here with Back in service.
      </p>
      <label style={{ display: 'block', marginTop: 14 }}>
        <span style={labelStyle}>Message on screen ({message.trim().length}/120)</span>
        <input data-testid="kiosk-cmd-oos1-message" value={message} onChange={e => setMessage(e.target.value.slice(0, 120))} autoFocus
          placeholder="e.g. Machine under maintenance — back tomorrow" onKeyDown={e => { if (e.key === 'Enter') submit(); }} style={{ ...inputStyle, marginTop: 6 }} />
      </label>
      <Footer onClose={onClose} onSend={submit} can={can} busy={busy} label="Take out of service" testId="kiosk-cmd-oos1" />
      {fleetModal}
    </ModalShell>
  );
}

export function BackInServiceDialog({ device, onClose, onDone }) {
  const { send, busy, fleetModal } = useIssue(device.kiosk_id, onDone);
  const submit = async () => { if (await send({ code: 'OOS0', args: {} })) onClose(); };
  return (
    <ModalShell title="Back in service" onClose={onClose} testId="kiosk-cmd-oos0">
      <p style={{ fontSize: 13, color: '#ccc', lineHeight: 1.5 }}>
        Clears the out-of-service notice on <b style={{ color: '#fff' }}>{device.name}</b>{device.config?.out_of_service_message ? <> (&quot;{device.config.out_of_service_message}&quot;)</> : null}. New sessions can start again as soon as the phone applies it.
      </p>
      <Footer onClose={onClose} onSend={submit} can busy={busy} label="Back in service" testId="kiosk-cmd-oos0" />
      {fleetModal}
    </ModalShell>
  );
}

// ── Rate / share ─────────────────────────────────────────────────────────────
export function SetRateDialog({ device, onClose, onDone }) {
  const current = device.config?.rate_centavos_per_hour || 0;
  const [pesos, setPesos] = useState(String(current / 100));
  const { send, busy, fleetModal } = useIssue(device.kiosk_id, onDone);
  const cents = pesosToCentavos(pesos);
  const can = Number.isInteger(cents) && cents >= 1 && cents <= 100000 * 100;
  const share = device.config?.owner_share_centavos_per_hour || 0;
  const submit = async () => { if (can && await send({ code: 'RATE', args: { centavos_per_hour: cents } })) onClose(); };
  return (
    <ModalShell title="Set rate" onClose={onClose} testId="kiosk-cmd-rate">
      <p style={{ fontSize: 12, color: '#a3a3a3', lineHeight: 1.5 }}>What customers pay per hour. Future sessions only — history keeps the rate it was started at.</p>
      <label style={{ display: 'block', marginTop: 14 }}>
        <span style={labelStyle}>Rate per hour (₱) — now {fmtCentavos(current)}</span>
        <input data-testid="kiosk-cmd-rate-input" value={pesos} onChange={e => setPesos(e.target.value)} inputMode="decimal" autoFocus
          onKeyDown={e => { if (e.key === 'Enter') submit(); }} style={{ ...inputStyle, marginTop: 6, fontFamily: 'Oswald, sans-serif', fontSize: 22 }} />
        <span style={hint}>Between ₱0.01 and ₱100,000. The phone applies its own bounds too.</span>
      </label>
      {can && cents < share && <p style={warn}>Below the HighMark share ({fmtCentavos(share)}/h): the court would owe on every hour. Lower the share first, or send a share command right after this.</p>}
      <Footer onClose={onClose} onSend={submit} can={can} busy={busy} label="Set rate" testId="kiosk-cmd-rate" />
      {fleetModal}
    </ModalShell>
  );
}

export function SetShareDialog({ device, onClose, onDone }) {
  const current = device.config?.owner_share_centavos_per_hour || 0;
  const rate = device.config?.rate_centavos_per_hour || 0;
  const [pesos, setPesos] = useState(String(current / 100));
  const { send, busy, fleetModal } = useIssue(device.kiosk_id, onDone);
  const cents = pesosToCentavos(pesos);
  const can = Number.isInteger(cents) && cents >= 0;
  const submit = async () => { if (can && await send({ code: 'SHARE', args: { owner_centavos_per_hour: cents } })) onClose(); };
  return (
    <ModalShell title="Set HighMark share" onClose={onClose} testId="kiosk-cmd-share">
      <p style={{ fontSize: 12, color: '#a3a3a3', lineHeight: 1.5 }}>The owner&apos;s share of every billed hour; the court keeps the rest. Must not exceed the rate ({fmtCentavos(rate)}/h, or a rate command still waiting for the phone).</p>
      <label style={{ display: 'block', marginTop: 14 }}>
        <span style={labelStyle}>Share per hour (₱) — now {fmtCentavos(current)}</span>
        <input data-testid="kiosk-cmd-share-input" value={pesos} onChange={e => setPesos(e.target.value)} inputMode="decimal" autoFocus
          onKeyDown={e => { if (e.key === 'Enter') submit(); }} style={{ ...inputStyle, marginTop: 6, fontFamily: 'Oswald, sans-serif', fontSize: 22 }} />
      </label>
      {can && cents > rate && <p style={warn}>Above the current rate — the server will refuse this unless a higher rate command is already waiting for the phone.</p>}
      <Footer onClose={onClose} onSend={submit} can={can} busy={busy} label="Set share" testId="kiosk-cmd-share" />
      {fleetModal}
    </ModalShell>
  );
}

// ── Court name (site) ────────────────────────────────────────────────────────
export function SetSiteDialog({ device, onClose, onDone }) {
  const [name, setName] = useState(device.site_name || '');
  const { send, busy, fleetModal } = useIssue(device.kiosk_id, onDone);
  const can = name.trim().length > 0 && name.trim().length <= 40;
  const submit = async () => { if (can && await send({ code: 'SITE', args: { site_name: name.trim() } })) onClose(); };
  return (
    <ModalShell title="Set court name" onClose={onClose} testId="kiosk-cmd-site">
      <p style={{ fontSize: 12, color: '#a3a3a3', lineHeight: 1.5 }}>The name the phone prints on its screen and stamps on every new session row (the server&apos;s site field follows once the phone acks).</p>
      <label style={{ display: 'block', marginTop: 14 }}>
        <span style={labelStyle}>Court name ({name.trim().length}/40)</span>
        <input data-testid="kiosk-cmd-site-input" value={name} onChange={e => setName(e.target.value.slice(0, 40))} autoFocus placeholder="e.g. Riverside Court"
          onKeyDown={e => { if (e.key === 'Enter') submit(); }} style={{ ...inputStyle, marginTop: 6 }} />
      </label>
      <Footer onClose={onClose} onSend={submit} can={can} busy={busy} label="Set court name" testId="kiosk-cmd-site" />
      {fleetModal}
    </ModalShell>
  );
}

// ── Staff PIN ────────────────────────────────────────────────────────────────
export function SetPinDialog({ device, onClose, onDone }) {
  const [pin, setPin] = useState('');
  const [again, setAgain] = useState('');
  const [show, setShow] = useState(false);
  const { send, busy, fleetModal } = useIssue(device.kiosk_id, onDone);
  const clean = pin.replace(/\D/g, '').slice(0, 6);
  const clean2 = again.replace(/\D/g, '').slice(0, 6);
  const can = clean.length === 6 && clean === clean2;
  const submit = async () => { if (can && await send({ code: 'PIN', args: { staff_pin: clean } })) onClose(); };
  const pinInput = (val, set, testId, label) => (
    <label style={{ display: 'block', marginTop: 12 }}>
      <span style={labelStyle}>{label}</span>
      <input data-testid={testId} value={val} onChange={e => set(e.target.value)} type={show ? 'text' : 'password'} inputMode="numeric" autoComplete="off" maxLength={6}
        onKeyDown={e => { if (e.key === 'Enter') submit(); }}
        style={{ ...inputStyle, marginTop: 6, fontFamily: 'Oswald, sans-serif', fontSize: 24, letterSpacing: 8, textAlign: 'center' }} />
    </label>
  );
  return (
    <ModalShell title="Set staff PIN" onClose={onClose} testId="kiosk-cmd-pin">
      <p style={{ fontSize: 12, color: '#a3a3a3', lineHeight: 1.5 }}>The court staff PIN on the phone (6 digits). The owner PIN is never set remotely. The PIN travels once to the phone and is stored nowhere on the server.</p>
      {pinInput(clean, setPin, 'kiosk-cmd-pin-input', 'New staff PIN')}
      {pinInput(clean2, setAgain, 'kiosk-cmd-pin-again', 'Again')}
      {clean2.length === 6 && clean !== clean2 && <p style={{ fontSize: 12, color: '#FF2D55', marginTop: 6 }}>The two entries differ.</p>}
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: 12, color: GREY, cursor: 'pointer' }}>
        <input type="checkbox" checked={show} onChange={e => setShow(e.target.checked)} /> Show digits
      </label>
      <p style={warn} data-testid="kiosk-cmd-pin-warning">
        The court&apos;s link follows this PIN: once the phone applies it, <b>{device.court_link?.code || 'the /m/ link'}</b> opens only with the new PIN. Tell the court before you send it.
      </p>
      <Footer onClose={onClose} onSend={submit} can={can} busy={busy} label="Set staff PIN" testId="kiosk-cmd-pin" />
      {fleetModal}
    </ModalShell>
  );
}

// ── Diagnostics ──────────────────────────────────────────────────────────────
export function DiagnosticsDialog({ device, onClose, onDone }) {
  const [lines, setLines] = useState('200');
  const { send, busy, fleetModal } = useIssue(device.kiosk_id, onDone);
  const n = Number(lines);
  const can = Number.isInteger(n) && n >= 1 && n <= 400;
  const submit = async () => { if (can && await send({ code: 'DIAG', args: { lines: n } })) onClose(); };
  return (
    <ModalShell title="Request diagnostics" onClose={onClose} testId="kiosk-cmd-diag">
      <p style={{ fontSize: 12, color: '#a3a3a3', lineHeight: 1.5 }}>The phone uploads the last N lines of its own log once. Read them from the command history (View).</p>
      <label style={{ display: 'block', marginTop: 14 }}>
        <span style={labelStyle}>Lines (1–400)</span>
        <input data-testid="kiosk-cmd-diag-lines" value={lines} onChange={e => setLines(e.target.value.replace(/\D/g, '').slice(0, 3))} inputMode="numeric" autoFocus
          onKeyDown={e => { if (e.key === 'Enter') submit(); }} style={{ ...inputStyle, marginTop: 6 }} />
      </label>
      <Footer onClose={onClose} onSend={submit} can={can} busy={busy} label="Request" testId="kiosk-cmd-diag" />
      {fleetModal}
    </ModalShell>
  );
}

export function DiagnosticsViewer({ command, onClose }) {
  const [text, setText] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    getDiagnostics(command.id).then(t => setText(typeof t === 'string' ? t : JSON.stringify(t, null, 1)))
      .catch(e => setError(errMsg(e, 'Could not load the diagnostics')));
  }, [command.id]);
  return (
    <ModalShell title={`Diagnostics · ${fmtMs(command.acked_at_ms)}`} onClose={onClose} testId="kiosk-diag-viewer">
      {error ? <p style={{ color: '#FF2D55', fontSize: 13 }}>{error}</p> : text == null ? <p style={{ color: GREY, fontSize: 13 }}>Loading…</p> : (
        <>
          <div className="flex justify-end mb-2">
            <button onClick={() => { navigator.clipboard?.writeText(text); toast.success('Copied'); }} data-testid="kiosk-diag-copy"
              style={{ ...ghostBtn, flex: 'none', padding: '6px 10px', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 6 }}><Copy className="h-3.5 w-3.5" /> Copy</button>
          </div>
          <pre data-testid="kiosk-diag-text" style={{ background: '#0A0A0A', border: '1px solid #2A2A2A', borderRadius: 8, padding: 12, fontSize: 11, color: '#ccc',
            maxHeight: '60vh', overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>{text}</pre>
        </>
      )}
    </ModalShell>
  );
}

// ── Command bar + history ────────────────────────────────────────────────────
const DIALOGS = {
  FE: ForceEndDialog, OOS1: OutOfServiceDialog, OOS0: BackInServiceDialog, RATE: SetRateDialog,
  SHARE: SetShareDialog, SITE: SetSiteDialog, PIN: SetPinDialog, DIAG: DiagnosticsDialog,
};
const BAR = [
  ['FE', OctagonX, '#FF2D55'], ['OOS1', PowerOff, AMBER], ['OOS0', Power, GREEN], ['RATE', Coins], ['SHARE', Percent],
  ['SITE', MapPin], ['PIN', KeyRound], ['DIAG', Stethoscope],
];

export function CommandBar({ device, onIssued }) {
  const [open, setOpen] = useState(null);
  const Dialog = open ? DIALOGS[open] : null;
  const disabled = device.status !== 'active';
  return (
    <div data-testid="kiosk-command-bar" style={{ background: '#141414', border: '1px solid #1E1E1E', borderRadius: 12, padding: 16, marginBottom: 16 }}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 15, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5 }}>Command</h2>
          <p style={{ fontSize: 12, color: GREY, marginTop: 2 }}>
            {device.push?.ok
              ? 'The phone is on push: it is nudged within seconds, executes, then acks with a result. If a nudge fails it still picks the command up on its next heartbeat (every 5 min while online).'
              : `Push cannot reach this phone (${NUDGE_REASON[device.push?.why] || 'not available'}), so it is delivered on the phone’s next heartbeat (every 2 min while online); it executes, then acks with a result.`}
            {' '}A newer command of the same kind replaces an unacked one.
          </p>
        </div>
        {!device.is_online && !disabled && <span style={{ fontSize: 12, color: AMBER }}>Phone offline — commands wait (force end for 15 min, the rest 24 h).</span>}
      </div>
      <div className="flex gap-2 flex-wrap mt-3">
        {BAR.map(([code, Icon, color]) => {
          const hide = (code === 'OOS1' && device.out_of_service) || (code === 'OOS0' && !device.out_of_service);
          if (hide) return null;
          return (
            <button key={code} onClick={() => setOpen(code)} disabled={disabled} data-testid={`kiosk-cmd-${code}`}
              style={{ background: 'none', border: `1px solid ${color ? color + '50' : '#2A2A2A'}`, borderRadius: 8, padding: '8px 12px', color: color || '#ccc',
                cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1, display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700 }}>
              <Icon className="h-4 w-4" /> {CODE_LABEL[code]}
            </button>
          );
        })}
      </div>
      {Dialog && <Dialog device={device} onClose={() => setOpen(null)} onDone={onIssued} />}
    </div>
  );
}

export function CommandHistory({ commands = [], testId = 'kiosk-device-commands' }) {
  const [viewing, setViewing] = useState(null);
  return (
    <div data-testid={testId} style={{ background: '#141414', border: '1px solid #1E1E1E', borderRadius: 12, padding: 18 }}>
      <h2 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 15, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Command history</h2>
      {commands.length === 0 ? (
        <div className="flex items-start gap-3">
          <Terminal className="h-4 w-4 shrink-0 mt-0.5" style={{ color: '#444' }} />
          <p style={{ fontSize: 13, color: '#555' }}>No commands yet. Use the Command bar above; the last 50 show here.</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
            <thead><tr style={{ borderBottom: '1px solid #1E1E1E' }}>
              {['Command', 'Args', 'Who', 'When', 'Status', 'Result'].map(h => <th key={h} style={{ ...labelStyle, textAlign: 'left', padding: '8px 10px', fontWeight: 700 }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {commands.map(c => {
                const s = commandState(c);
                const nudge = describeNudge(c.nudge);
                const cell = { padding: '8px 10px', fontSize: 12, verticalAlign: 'top' };
                return (
                  <tr key={c.id} data-testid={`kiosk-command-${c.id}`} style={{ borderBottom: '1px solid #1E1E1E' }}>
                    <td style={{ ...cell, color: '#fff', fontWeight: 700, whiteSpace: 'nowrap' }}>{CODE_LABEL[c.code] || c.code}</td>
                    <td style={{ ...cell, color: '#ccc' }}>{describeArgs(c)}</td>
                    <td style={{ ...cell, color: GREY }}>{c.issued_by_name || '—'}</td>
                    <td style={{ ...cell, color: GREY, whiteSpace: 'nowrap' }} title={c.acked_at_ms ? `acked ${fmtMs(c.acked_at_ms)}` : c.delivered_at_ms ? `delivered ${fmtMs(c.delivered_at_ms)}` : ''}>{fmtMs(c.issued_at_ms)}</td>
                    <td style={cell}>
                      <span data-testid={`kiosk-command-state-${s.key}`} style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5, color: s.color, background: s.color + '18', padding: '2px 7px', borderRadius: 999, whiteSpace: 'nowrap' }}>{s.label}</span>
                      {nudge && <div data-testid={`kiosk-command-nudge-${nudge.ok ? 'ok' : 'failed'}`} style={{ fontSize: 11, color: nudge.ok ? GREEN : AMBER, marginTop: 4, whiteSpace: 'nowrap' }}>{nudge.text}</div>}
                    </td>
                    <td style={{ ...cell, color: c.status === 'failed' ? '#FF2D55' : GREY }}>
                      {describeResult(c)}
                      {c.code === 'DIAG' && c.status === 'done' && (
                        <button onClick={() => setViewing(c)} data-testid={`kiosk-command-view-${c.id}`}
                          style={{ background: 'none', border: 'none', color: GREEN, cursor: 'pointer', fontSize: 12, fontWeight: 700, marginLeft: 8, display: 'inline-flex', alignItems: 'center', gap: 4, padding: 0 }}>
                          <Eye className="h-3.5 w-3.5" /> View
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {viewing && <DiagnosticsViewer command={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}
